import Compressor from 'compressorjs'
import {FFmpeg} from '@ffmpeg/ffmpeg'
import {fetchFile, toBlobURL} from '@ffmpeg/util'
import {useRef, useCallback, useMemo} from 'react'

interface CompressionOptions {
  quality?: number
  maxWidth?: number
  maxHeight?: number
  convertSize?: number
}

interface VideoCompressionOptions {
  crf?: number
  preset?: string
  maxSize?: number
  signal?: AbortSignal
  onProgress?: (percentage: number) => void
}

const useCompress = () => {
  const ffmpegRef = useRef<FFmpeg | null>(null)
  const isLoadingRef = useRef(false)
  const loadPromiseRef = useRef<Promise<void> | null>(null)

  // Memoize FFmpeg base URL to avoid recalculation
  const baseURL = useMemo(() => 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd', [])

  // Enhanced image compression with better error handling and options
  const compressImages = useCallback(async (
    image: File, 
    options: CompressionOptions = {}
  ): Promise<Blob> => {
    const {
      quality = 0.8,
      maxWidth = 1920,
      maxHeight = 1080,
      convertSize = 5 * 1024 * 1024 // 5MB
    } = options

    // Validate input
    if (!image || !(image instanceof File)) {
      throw new Error('Invalid image file provided')
    }

    if (!image.type.startsWith('image/')) {
      throw new Error('File is not a valid image')
    }

    try {
      const compressedResult = await new Promise<Blob>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Image compression timeout'))
        }, 30000) // 30 second timeout

        new Compressor(image, {
          quality,
          maxWidth,
          maxHeight,
          convertSize,
          convertTypes: ['image/png', 'image/webp'],
          beforeDraw(context, canvas) {
            // Ensure proper canvas context
            if (!context || !canvas) {
              reject(new Error('Invalid canvas context'))
              return false
            }
            return true
          },
          success: (result: Blob) => {
            clearTimeout(timeout)
            
            // Validate result
            if (!result || result.size === 0) {
              reject(new Error('Compression resulted in empty file'))
              return
            }

            // Ensure we got compression benefit or original was small
            if (result.size > image.size && image.size > convertSize) {
              console.warn('Compressed file is larger than original, using original')
              resolve(image)
            } else {
              resolve(result)
            }
          },
          error: (error: Error) => {
            clearTimeout(timeout)
            console.error('Image compression failed:', error)
            reject(new Error(`Image compression failed: ${error.message}`))
          },
        })
      })

      return compressedResult
    } catch (error) {
      console.error('Image compression error:', error)
      // Fallback to original file if compression fails
      throw error
    }
  }, [])

  // Initialize FFmpeg with proper error handling and singleton pattern
  const initializeFFmpeg = useCallback(async (signal?: AbortSignal): Promise<FFmpeg> => {
    // Return existing instance if already loaded
    if (ffmpegRef.current && ffmpegRef.current.loaded) {
      return ffmpegRef.current
    }

    // If already loading, wait for existing load promise
    if (isLoadingRef.current && loadPromiseRef.current) {
      await loadPromiseRef.current
      if (ffmpegRef.current && ffmpegRef.current.loaded) {
        return ffmpegRef.current
      }
    }

    // Set loading flag
    isLoadingRef.current = true

    const ffmpeg = new FFmpeg()
    ffmpegRef.current = ffmpeg

    // Create load promise
    loadPromiseRef.current = (async () => {
      try {
        if (signal?.aborted) {
          throw new DOMException('Operation aborted', 'AbortError')
        }

        // Load FFmpeg with proper error handling
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      })

        console.log('FFmpeg loaded successfully')
      } catch (error) {
        console.error('FFmpeg initialization failed:', error)
        ffmpegRef.current = null
        throw error
      } finally {
        isLoadingRef.current = false
      }
    })()

    await loadPromiseRef.current
    return ffmpeg
  }, [baseURL])

  // Enhanced video compression with better options and error handling
  const compressMedia = useCallback(async (
    file: File, 
    options: VideoCompressionOptions = {},
    signal?: AbortSignal
  ): Promise<Blob> => {
    const {
      crf = 28,
      preset = 'medium',
      onProgress: onProgressCallback,
      // Removed maxSize limit - compressor should handle any file size
    } = options

    console.log('compressMedia called with:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      options
    })

    // Validate input
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid video file provided')
    }

    if (!file.type.startsWith('video/')) {
      throw new Error('File is not a valid video')
    }

    // For files under 50MB, skip compression to avoid overhead
    if (file.size < 50 * 1024 * 1024) {
      console.log('File is under 50MB, skipping compression')
      return file
    }

    try {
      console.log('Initializing FFmpeg...')
      const ffmpeg = await initializeFFmpeg(signal)
      console.log('FFmpeg initialized successfully')

      if (signal?.aborted) {
        throw new DOMException('Operation aborted', 'AbortError')
      }

      // Generate unique filenames to avoid conflicts
      const timestamp = Date.now()
      const inputFileName = `input_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
      const outputFileName = `output_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`

      console.log('Generated filenames:', { inputFileName, outputFileName })

      try {
        // Write input file
        console.log('Writing input file to FFmpeg...')
        await ffmpeg.writeFile(inputFileName, await fetchFile(file))
        console.log('Input file written successfully')

        if (signal?.aborted) {
          throw new DOMException('Operation aborted', 'AbortError')
        }

        // Intelligent compression settings based on file size
        let compressionArgs: string[]
        const sizeMB = file.size / (1024 * 1024)
        
        if (sizeMB > 500) { // > 500MB - extremely large files
          console.log(`Using maximum compression for extremely large file (${sizeMB.toFixed(2)}MB)`)
          compressionArgs = [
            '-i', inputFileName,
            '-c:v', 'libx264',
            '-preset', 'ultrafast', // Fastest encoding
            '-crf', '35', // Maximum compression
            '-c:a', 'aac',
            '-b:a', '64k', // Minimal audio bitrate
            '-movflags', '+faststart',
            '-vf', 'scale=min(854\\,iw):min(480\\,ih)', // 480p resolution
            '-r', '20', // Lower frame rate
            '-maxrate', '500k', // Bitrate limit
            '-bufsize', '1000k',
            '-threads', '0', // Use all available CPU cores
            '-y',
            outputFileName
          ]
        } else if (sizeMB > 200) { // > 200MB - very large files
          console.log(`Using aggressive compression for very large file (${sizeMB.toFixed(2)}MB)`)
          compressionArgs = [
            '-i', inputFileName,
            '-c:v', 'libx264',
            '-preset', 'veryfast',
            '-crf', '32',
            '-c:a', 'aac',
            '-b:a', '96k',
            '-movflags', '+faststart',
            '-vf', 'scale=min(1080\\,iw):min(720\\,ih)', // 720p resolution
            '-r', '24',
            '-maxrate', '1M',
            '-bufsize', '2M',
            '-threads', '0',
            '-y',
            outputFileName
          ]
        } else if (sizeMB > 100) { // > 100MB - large files
          console.log(`Using high compression for large file (${sizeMB.toFixed(2)}MB)`)
          compressionArgs = [
            '-i', inputFileName,
            '-c:v', 'libx264',
            '-preset', 'fast',
            '-crf', '30',
            '-c:a', 'aac',
            '-b:a', '128k',
            '-movflags', '+faststart',
            '-vf', 'scale=min(1280\\,iw):min(720\\,ih)',
            '-threads', '0',
            '-y',
            outputFileName
          ]
        } else if (sizeMB > 50) { // > 50MB - medium-large files
          console.log(`Using medium-high compression for medium-large file (${sizeMB.toFixed(2)}MB)`)
          compressionArgs = [
            '-i', inputFileName,
            '-c:v', 'libx264',
            '-preset', 'fast',
            '-crf', crf.toString(),
            '-c:a', 'aac',
            '-b:a', '192k',
            '-movflags', '+faststart',
            '-vf', 'scale=min(1920\\,iw):min(1080\\,ih)', // Full HD
            '-threads', '0',
            '-y',
            outputFileName
          ]
        } else if (sizeMB > 20) { // > 20MB - medium files
          console.log(`Using medium compression for medium file (${sizeMB.toFixed(2)}MB)`)
          compressionArgs = [
            '-i', inputFileName,
            '-c:v', 'libx264',
            '-preset', 'fast',
            '-crf', crf.toString(),
            '-c:a', 'aac',
            '-b:a', '192k',
            '-movflags', '+faststart',
            '-threads', '0',
            '-y',
            outputFileName
          ]
        } else {
          // Light compression for smaller files
          console.log(`Using light compression for small file (${sizeMB.toFixed(2)}MB)`)
          compressionArgs = [
            '-i', inputFileName,
            '-c:v', 'libx264',
            '-preset', 'ultrafast',
            '-crf', '23',
            '-c:a', 'copy',
            '-movflags', '+faststart',
            '-threads', '0',
            '-y',
            outputFileName
          ]
        }

        console.log('Compression arguments:', compressionArgs)

        // Execute compression with progress monitoring
        await new Promise<void>((resolve, reject) => {
          let progressReported = false
          let isResolved = false

          const cleanup = () => {
            ffmpeg.off('progress', onProgress)
            ffmpeg.off('log', onLog)
          }

          const onProgress = (progress: any) => {
            progressReported = true
            let percentage = 0
            
            // Try different progress properties
            if (progress.progress !== undefined) {
              percentage = Math.round(progress.progress * 100)
            } else if (progress.ratio !== undefined) {
              percentage = Math.round(progress.ratio * 100)
            } else if (progress.percent !== undefined) {
              percentage = Math.round(progress.percent)
            }
            
            console.log('Compression progress:', percentage + '%')
            
            // Call the onProgress callback if provided
            if (onProgressCallback && percentage > 0) {
              onProgressCallback(percentage)
            }
          }

          const onLog = (log: any) => {
            console.log('FFmpeg log:', log.message)
            
            // Parse progress from log message as fallback
            const progressMatch = log.message.match(/frame=\s*(\d+).*fps=.*time=(\d{2}):(\d{2}):(\d{2}\.\d{2})/);
            if (progressMatch && onProgressCallback) {
              const [, frame, hours, minutes, seconds] = progressMatch;
              const currentTimeSeconds = parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseFloat(seconds);
              
              // Estimate total duration (rough approximation for progress)
              // This is a fallback when we don't have exact duration
              const estimatedDuration = sizeMB * 6; // Rough estimate: 6 seconds per MB for calculation
              const progressPercentage = Math.min(Math.round((currentTimeSeconds / estimatedDuration) * 100), 99);
              
              if (progressPercentage > 0 && progressPercentage <= 100) {
                console.log(`Compression progress from log: ${progressPercentage}%`);
                onProgressCallback(progressPercentage);
              }
            }
            
            if (log.type === 'fferr' && log.message.includes('Error')) {
              cleanup()
              if (!isResolved) {
                isResolved = true
                reject(new Error(`FFmpeg error: ${log.message}`))
              }
            }
          }

          ffmpeg.on('progress', onProgress)
          ffmpeg.on('log', onLog)

          ffmpeg.exec(compressionArgs)
            .then(() => {
              cleanup()
              if (!isResolved) {
                isResolved = true
                resolve()
              }
            })
            .catch((error) => {
              cleanup()
              if (!isResolved) {
                isResolved = true
                reject(error)
              }
            })

          // Check for abort signal periodically
          const abortChecker = setInterval(() => {
            if (signal?.aborted) {
              cleanup()
              clearInterval(abortChecker)
              if (!isResolved) {
                isResolved = true
                reject(new DOMException('Operation aborted', 'AbortError'))
              }
            }
          }, 1000)

          // Add timeout for very long operations - dynamic based on file size
          const baseTimeout = 300000 // 5 minutes base
          let timeout: number
          
          if (sizeMB > 500) {
            timeout = 900000 // 15 minutes for extremely large files
          } else if (sizeMB > 200) {
            timeout = 600000 // 10 minutes for very large files
          } else if (sizeMB > 100) {
            timeout = 450000 // 7.5 minutes for large files
          } else {
            timeout = baseTimeout // 5 minutes for normal files
          }
          
          const timeoutHandle = setTimeout(() => {
            cleanup()
            clearInterval(abortChecker)
            if (!isResolved) {
              isResolved = true
              reject(new Error(`Video compression timeout after ${timeout / 60000} minutes. File may be too large or complex.`))
            }
          }, timeout)

          // Cleanup function to clear timers
          const cleanupTimers = () => {
            clearTimeout(timeoutHandle)
            clearInterval(abortChecker)
          }

          // Override resolve/reject to ensure cleanup
          const originalResolve = resolve
          const originalReject = reject
          
          resolve = (...args) => {
            cleanupTimers()
            originalResolve(...args)
          }
          
          reject = (...args) => {
            cleanupTimers()
            originalReject(...args)
          }
        })

        if (signal?.aborted) {
          throw new DOMException('Operation aborted', 'AbortError')
        }

        // Read compressed file
        const compressedData = await ffmpeg.readFile(outputFileName)

        if (!compressedData || (compressedData as Uint8Array).length === 0) {
          throw new Error('Compression resulted in empty file')
        }

        // Create blob with proper mime type
        const mimeType = file.type || 'video/mp4'
        const compressedBlob = new Blob([compressedData], { type: mimeType })

        // Validate compression results
        if (compressedBlob.size === 0) {
          throw new Error('Compression resulted in empty blob')
        }

        // Check if compression was beneficial
        const compressionRatio = compressedBlob.size / file.size
        console.log(`Compression ratio: ${(compressionRatio * 100).toFixed(2)}%`)

        // If compression didn't help much and file was already small, return original
        if (compressionRatio > 0.9 && file.size < 10 * 1024 * 1024) {
          console.log('Compression not beneficial, returning original file')
          return file
        }

        // Preserve file metadata
        Object.defineProperty(compressedBlob, 'name', {
          value: file.name,
          writable: false
        })

        Object.defineProperty(compressedBlob, 'lastModified', {
          value: file.lastModified,
          writable: false
      })

      return compressedBlob

      } finally {
        // Clean up temporary files
        try {
          await ffmpeg.deleteFile(inputFileName).catch(() => {})
          await ffmpeg.deleteFile(outputFileName).catch(() => {})
        } catch (cleanupError) {
          console.warn('Failed to cleanup temporary files:', cleanupError)
        }
      }

    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Video compression aborted')
      throw error
      }
      
      console.error('Video compression failed:', error)
      throw new Error(`Video compression failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [initializeFFmpeg])

  // Cleanup function to free resources
  const cleanup = useCallback(() => {
    if (ffmpegRef.current) {
      try {
        // FFmpeg cleanup if available
        ffmpegRef.current = null
      } catch (error) {
        console.warn('Error during FFmpeg cleanup:', error)
      }
    }
  }, [])

  return {
    compressImages,
    compressMedia,
    cleanup,
    isLoading: isLoadingRef.current
  }
}

export default useCompress
